import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CheckCircle2, Image as ImageIcon, AlertCircle, Star, Camera, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { 
  getSelectionRuleForProduct, 
  findConflictingLineItem, 
  canAddMoreOfProduct 
} from "@/utils/productSelectionRules";
import { getCategoryConfig } from "@/hooks/useCategoryConfig";
import type { CategoryConfig } from "@/utils/categoryMeasurementMapping";

interface Product {
  id: string;
  name: string;
  brand?: string;
  category: string;
  unit: string;
  image_url?: string;
  showcase_image_url?: string;
  customer_description?: string;
  selling_points?: string[];
}

interface ProductPrice {
  product_id: string;
  unit_price: number;
}

interface MeasurementVars {
  area_sqft: number;
  eave_lf: number;
  rake_lf: number;
  valley_lf: number;
  hip_lf: number;
  ridge_lf: number;
  sidewall_lf: number;
  headwall_lf: number;
  flashing_lf: number;
  chimney_count: number;
  skylight_count: number;
  pipe_1_5_count: number;
  pipe_3_count: number;
  layers: number;
}

interface LineItem {
  id?: string;
  product_id: string;
  description: string;
  calculation_source?: string;
}

interface ProductCardGridProps {
  products: Product[];
  productPrices: ProductPrice[];
  selectedProductIds: Set<string>;
  onProductSelect: (product: Product, price: number) => void;
  priceBookId: string;
  measurementVars?: MeasurementVars;
  lineItems?: LineItem[];
  categoryConfigs?: CategoryConfig[];
  defaultProductId?: string | null;
}

export function ProductCardGrid({
  products,
  productPrices,
  selectedProductIds,
  onProductSelect,
  measurementVars,
  lineItems = [],
  categoryConfigs = [],
  defaultProductId,
}: ProductCardGridProps) {
  const [showcasePreview, setShowcasePreview] = useState<{
    open: boolean;
    imageUrl: string;
    productName: string;
    productBrand?: string;
  } | null>(null);
  const [pressTimer, setPressTimer] = useState<NodeJS.Timeout | null>(null);

  const getPrice = (productId: string) => {
    const priceRecord = productPrices.find(p => p.product_id === productId);
    return priceRecord?.unit_price || 0;
  };

  const handleMouseDown = (product: Product, showcaseUrl: string) => {
    const timer = setTimeout(() => {
      setShowcasePreview({
        open: true,
        imageUrl: showcaseUrl,
        productName: product.name,
        productBrand: product.brand,
      });
    }, 500); // 500ms long press
    
    setPressTimer(timer);
  };

  const handleMouseUp = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
  };

  const isGutterProduct = (product: Product) => {
    return product.category.toLowerCase().includes("gutter") && 
           (product.name.toLowerCase().includes("k-style") || 
            product.name.toLowerCase().includes("gutter")) &&
           !product.name.toLowerCase().includes("downspout");
  };

  const isDownspoutProduct = (product: Product) => {
    return product.name.toLowerCase().includes("downspout");
  };

  const getAutoQuantityHint = (product: Product) => {
    if (!measurementVars) return null;
    
    if (isGutterProduct(product)) {
      return measurementVars.eave_lf > 0 
        ? `Auto: ${measurementVars.eave_lf} LF from eaves`
        : null;
    }
    
    if (isDownspoutProduct(product)) {
      return "Manual entry required";
    }
    
    return null;
  };

  const getSelectionStatus = (product: Product) => {
    const isSelected = selectedProductIds.has(product.id);
    
    return {
      isSelected,
      canSelect: true,
      currentCount: 0,
      maxAllowed: Infinity,
    };
  };

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <ImageIcon className="w-16 h-16 mb-4 opacity-20" />
        <p className="text-lg">No products available in this phase</p>
        <p className="text-sm">Products will appear here once added</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
        {products.map((product) => {
          const price = getPrice(product.id);
          const autoQuantityHint = getAutoQuantityHint(product);
      const status = getSelectionStatus(product);
      const { isSelected, canSelect } = status;

          return (
            <Card
              key={product.id}
              className={cn(
                "group relative overflow-hidden transition-all duration-200",
                "min-h-[320px] flex flex-col",
                "cursor-pointer hover:shadow-lg",
                isSelected && "ring-2 ring-primary shadow-lg"
              )}
              onClick={canSelect ? () => onProductSelect(product, price) : undefined}
              onMouseDown={() => {
                if (product.showcase_image_url) {
                  handleMouseDown(product, product.showcase_image_url);
                }
              }}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={() => {
                if (product.showcase_image_url) {
                  handleMouseDown(product, product.showcase_image_url);
                }
              }}
              onTouchEnd={handleMouseUp}
            >
            {/* Product Image */}
            <div className="relative w-full aspect-square bg-muted flex items-center justify-center overflow-hidden">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                />
              ) : (
                <ImageIcon className="w-20 h-20 text-muted-foreground/30" />
              )}
              
              {/* Selection Overlay */}
              {isSelected && (
                <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                  <div className="bg-primary text-primary-foreground rounded-full p-3">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                </div>
              )}

              {/* Status Badges */}
              {defaultProductId === product.id && !isSelected && (
                <Badge
                  variant="secondary"
                  className="absolute top-2 right-14 text-xs bg-primary/10 border-primary/20 z-10"
                >
                  <Star className="w-3 h-3 mr-1 fill-primary text-primary" />
                  Default
                </Badge>
              )}


              {/* Brand Badge */}
              {product.brand && (
                <Badge
                  variant="secondary"
                  className="absolute top-2 right-2 text-xs z-10"
                >
                  {product.brand}
                </Badge>
              )}

              {/* Showcase indicator badge */}
              {product.showcase_image_url && (
                <Badge
                  variant="outline"
                  className="absolute bottom-2 left-2 text-xs bg-background/90 backdrop-blur-sm z-10 pointer-events-none"
                >
                  <Camera className="w-3 h-3 mr-1" />
                  Hold to view
                </Badge>
              )}
            </div>

            {/* Product Info */}
            <div className="flex-1 p-4 flex flex-col">
              <h3 className="font-semibold text-base md:text-lg line-clamp-2 mb-2">
                {product.name}
              </h3>

              {product.customer_description && (
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {product.customer_description}
                </p>
              )}

              {/* Selling Points */}
              {product.selling_points && Array.isArray(product.selling_points) && product.selling_points.length > 0 && (
                <ul className="text-xs text-muted-foreground space-y-1 mb-3">
                  {(product.selling_points as string[]).slice(0, 2).map((point, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="mr-1">•</span>
                      <span className="line-clamp-1">{point}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Auto Quantity Hint */}
              {autoQuantityHint && (
                <div className="mb-2">
                  <Badge variant="secondary" className="text-xs">
                    {autoQuantityHint}
                  </Badge>
                </div>
              )}

              {/* Price */}
              <div className="mt-auto pt-3 border-t">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold text-primary">
                      ${price.toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      per {product.unit}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant={isSelected ? "default" : "outline"}
                    className="min-w-[44px] min-h-[44px]"
                  >
                    {isSelected ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Selected
                      </>
                    ) : (
                      "Select"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        );
      })}
      </div>

      {/* Fullscreen Showcase Preview Dialog */}
      {showcasePreview?.open && (
        <Dialog open={showcasePreview.open} onOpenChange={(open) => {
          if (!open) setShowcasePreview(null);
        }}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 bg-black">
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Close button */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-50 bg-white/10 hover:bg-white/20 text-white"
                onClick={() => setShowcasePreview(null)}
              >
                <X className="w-6 h-6" />
              </Button>
              
              {/* Product info overlay */}
              <div className="absolute top-4 left-4 z-50 bg-black/70 backdrop-blur-sm rounded-lg px-4 py-2 text-white">
                {showcasePreview.productBrand && (
                  <p className="text-sm font-medium opacity-80">{showcasePreview.productBrand}</p>
                )}
                <p className="text-lg font-bold">{showcasePreview.productName}</p>
                <p className="text-xs opacity-60">Installation Example</p>
              </div>
              
              {/* Full screen image */}
              <img
                src={showcasePreview.imageUrl}
                alt={`${showcasePreview.productName} installation`}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
